import type { CalculatorContent, Faq } from '@/types/calculator'
import type { BondInputs } from './types'

export const content: CalculatorContent<BondInputs> = {
  intro:
    'Estimate your monthly bond repayment with this bond repayment and home loan calculator for South Africa. Enter the property price, deposit, interest rate and term to see the instalment, total interest and a simple affordability signal.',
  howItWorks: [
    'The loan amount is the property price minus your deposit. The monthly repayment uses the standard amortising-bond formula: a fixed interest rate compounded monthly over the full term.',
    'Total repayment is the instalment multiplied by the number of months; total interest is the difference between that and the loan amount — often more than the property price over a 20-year term.',
    'The affordability signal compares the repayment and your existing debt to your gross monthly income using the debt-to-income limit you set. Crossing roughly a third of income flags "maybe"; higher levels flag "no".',
    'This is an estimate only — banks add initiation, admin and insurance fees, and approval depends on their own credit criteria.',
  ],
  formulas: [
    {
      label: 'Loan amount',
      expression: 'property price − deposit',
    },
    {
      label: 'Monthly repayment',
      expression: 'L × r × (1 + r)ⁿ ÷ ((1 + r)ⁿ − 1)',
      notes: 'L = loan amount, r = annual rate ÷ 12, n = term in months.',
    },
    {
      label: 'Total interest',
      expression: 'monthly repayment × n − loan amount',
    },
    {
      label: 'Debt-to-income',
      expression: '(repayment + existing debt) ÷ monthly gross income',
    },
  ],
  example: {
    title: 'A R1.5 million home with a 10% deposit',
    description:
      'A buyer purchases a property with a deposit over a 20-year term at the default interest rate, and checks the repayment against household income.',
    inputs: {
      propertyPrice: 1_500_000,
      deposit: 150_000,
      interestRate: 0.115,
      termYears: 20,
      monthlyIncome: 50_000,
      monthlyDebt: 5_000,
      maxDebtToIncomeRatio: 0.3,
    },
  },
  definitions: [
    {
      term: 'Bond',
      description:
        'A home loan registered over a property in South Africa, repaid in monthly instalments over a term — usually 20 to 30 years.',
    },
    {
      term: 'Deposit',
      description:
        'The upfront cash portion of the purchase price. A larger deposit lowers the loan amount, instalment and total interest.',
    },
    {
      term: 'Amortisation',
      description:
        'Paying off a loan in equal instalments. Early payments are mostly interest; the capital portion grows over time.',
    },
    {
      term: 'Debt-to-income ratio',
      description:
        'Total monthly debt repayments divided by gross monthly income — a common affordability rule of thumb used by lenders.',
    },
    {
      term: 'Interest rate',
      description:
        'The annual rate charged on the outstanding balance, usually linked to the prime lending rate.',
    },
  ],
}

export const faqs: Faq[] = [
  {
    question: 'Is this a home-loan approval?',
    answer:
      'No. Lenders use their own criteria, interest rates and fees. This calculator gives an estimate only.',
  },
  {
    question: 'How much bond can I afford?',
    answer:
      'A common guideline keeps total debt repayments below about a third of gross income. Enter your income and debts to see the affordability signal for a specific property.',
  },
  {
    question: 'How does the deposit affect my repayment?',
    answer:
      'A bigger deposit reduces the loan amount, which lowers both the monthly instalment and the total interest paid over the term.',
  },
  {
    question: 'What costs are not included?',
    answer:
      'Bond initiation, admin and insurance fees are excluded, as are transfer duty and attorney costs — use the transfer cost calculator for those.',
  },
  {
    question: 'Is a longer term better?',
    answer:
      'A longer term lowers the monthly instalment but increases total interest substantially. Compare 20- and 30-year terms to see the difference.',
  },
]
